import type { ReactNode } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { AppShell, KrwProvider, useIsWide } from '@/ui'
import type { DetailState } from '@/lib/detail'
import Login from './routes/Login'
import AppList from './routes/apps/AppList'
import AppRoute from './routes/apps/AppDrawer'
import CalendarPage from './routes/calendar/Calendar'
import CountriesPage from './routes/countries/Countries'
import CampaignsPage from './routes/campaigns/Campaigns'
import Today from './routes/today/Today'
import Alerts from './routes/alerts/Alerts'
import GooglePage from './routes/google/GoogleConnect'
import Jobs from './routes/system/Jobs'
import Settings from './routes/system/Settings'
import { isRedirected, redirectRoutes } from './routes/_redirects'

/** The pages at their addresses (plan 1-2: no address moves). */
const ROUTES: { path: string; element: ReactNode }[] = [
  { path: '/', element: <Today /> },
  { path: '/calendar', element: <CalendarPage /> },
  { path: '/alerts', element: <Alerts /> },
  { path: '/apps', element: <AppList /> },
  // An app's detail. Opened from a list it is drawn over that list (see
  // DETAIL_ROUTES); opened by its address it brings the list itself.
  { path: '/apps/:id', element: <AppRoute /> },
  { path: '/countries', element: <CountriesPage /> },
  { path: '/campaigns', element: <CampaignsPage /> },
  // Google Console's registered OAuth redirect URI: it comes back here with
  // ?code=&scope=&state=. It never moves.
  { path: '/settings/google', element: <GooglePage /> },
  { path: '/jobs', element: <Jobs /> },
  { path: '/settings', element: <Settings /> },
]

/** The details drawn over the page they were opened from, from 1024px up. */
const DETAIL_ROUTES: { path: string; element: ReactNode }[] = [{ path: '/apps/:id', element: <AppRoute overlay /> }]

const MOUNTED = ROUTES.filter((r) => !isRedirected(r.path))

export const MOUNTED_PATHS = MOUNTED.map((r) => r.path)

export default function App() {
  const location = useLocation()
  const wide = useIsWide()
  // A detail opened from a page carries that page as its background: on a
  // wide screen the page stays drawn (and keeps its filters and scroll) with
  // the detail as a drawer on top. Below 1024px the detail is a page.
  const background = wide && DETAIL_ROUTES.length > 0 ? (location.state as DetailState | null)?.background : undefined
  return (
    <>
      <Routes location={background ?? location}>
        <Route path="/login" element={<Login />} />
        <Route element={<AppShell />}>
          {MOUNTED.map((r) => (
            <Route key={r.path} path={r.path} element={r.element} />
          ))}
          {redirectRoutes()}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
      {background && (
        // Drawn outside the shell, so it takes the won rate itself.
        <KrwProvider>
          <Routes>
            {DETAIL_ROUTES.map((r) => (
              <Route key={r.path} path={r.path} element={r.element} />
            ))}
            <Route path="*" element={null} />
          </Routes>
        </KrwProvider>
      )}
    </>
  )
}
